import { createFileRoute, redirect } from "@tanstack/react-router";

import { buildHref } from "@softmax/web-addresses";
import { canonicalSiteUrl } from "@softmax/web-addresses/discovery";
import { WatchEntryPage } from "@/features/watch";
import { WatchLoading, readWatchSearch } from "@/features/watch/navigation";
import {
  getStoredWatchLeague,
  watchEntryQueryOptions,
} from "@/features/watch/records";

import { ProductRouteError } from "@/session/product-route-frame";

const description = "Theater-style replay viewing of recent league matches.";

export const Route = createFileRoute("/_site/watch")({
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: ({ search, location }) => {
    const episode = "episode" in search ? search.episode : undefined;

    if (typeof episode !== "string" || !episode.trim()) return;

    const parameters = new URLSearchParams(location.searchStr);
    parameters.delete("episode");
    throw redirect({
      href: buildHref({
        name: "requestWatch",
        requestId: episode,
        search: parameters,
      }),
      statusCode: 307,
    });
  },
  loaderDeps: ({ search }) => {
    const league = Array.isArray(search.league)
      ? search.league[0]
      : search.league;
    const kiosk = Array.isArray(search.kiosk) ? search.kiosk[0] : search.kiosk;
    const values = readWatchSearch({
      league: typeof league === "string" ? league : undefined,
      kiosk: typeof kiosk === "string" ? kiosk : undefined,
    });
    return {
      leagueId: values.league,
      globalKiosk: values.kiosk !== null && values.league === null,
    };
  },
  shouldReload: ({ cause }) => cause !== "stay",
  loader: ({ context, deps }) => {
    const selection = {
      ...deps,
      stored: getStoredWatchLeague(),
    };
    const options = watchEntryQueryOptions(selection);

    void context.queryClient.prefetchQuery(options);

    return selection;
  },
  head: () => ({
    meta: [
      { title: "Watch · Softmax" },
      { name: "description", content: description },
      { property: "og:title", content: "Watch · Softmax" },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalSiteUrl("/watch") },
      {
        property: "og:image",
        content: canonicalSiteUrl("/watch/opengraph-image"),
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Watch league matches on Softmax" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Watch · Softmax" },
      { name: "twitter:description", content: description },
      {
        name: "twitter:image",
        content: canonicalSiteUrl("/watch/twitter-image"),
      },
    ],
    links: [{ rel: "canonical", href: canonicalSiteUrl("/watch") }],
  }),
  component: WatchPage,
  pendingComponent: WatchLoading,
  errorComponent: ProductRouteError,
});

function WatchPage() {
  return <WatchEntryPage selection={Route.useLoaderData()} />;
}
